import moment from 'moment/moment';
import {
  Chip,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DefaultButton,
  PrimaryButton,
  TextField,
  Typography,
  Tooltip,
  createStyles,
  makeStyles,
} from '@bb-ui/react-library';
import React from 'react';
import { OpenInNewWindow } from '@bb-ui/icons/dist/small';
import { Faster, Slower, User, People, Calendar, Refresh } from '@bb-ui/icons/dist/medium';
import { useHistory } from 'react-router-dom';
import { JobCardList } from './basic/JobCardList';
import { JobStatusIcon } from '../../JobStatusIcon/JobStatusIcon';
import { ExecutionDetailResponseBody, Status } from '@lct-jobs/core';
import { useTranslation } from 'react-i18next';
import useRestApi from '../../../hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { FAILED_JOB_STATUSES } from '../failedJobStatuses';

const useStyles = makeStyles((theme) =>
  createStyles({
    chipActions: {
      display: 'flex',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
      padding: '8px 16px 4px',
    },
    headerActions: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    topSpacing: {
      marginTop: theme.spacing(1),
    },
    listItem: {
      paddingTop: 6,
      paddingBottom: 6,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      },
    },
  }),
);

export interface ExecutionPropsBox {
  jobExecution?: ExecutionDetailResponseBody;
  detailsLoading?: boolean;
  siteDetailsLoading?: boolean;
  onRescheduleSuccess?: () => void;
  onCancelSuccess?: () => void;
  onRedriveSuccess?: () => void;
  /** True when the execution belongs to a LearnInstance tenant. */
  isLearnInstanceExecution?: boolean;
}

export const ExecutionBox: React.FC<ExecutionPropsBox> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const execution = props.jobExecution;
  const loadingDetails = props.detailsLoading;
  const { siteDetailsLoading } = props;
  const startDate = execution?.startDate ? moment(execution.startDate) : undefined;
  const endDate = execution?.endDate ? moment(execution.endDate) : undefined;
  const domain =
    execution?.region === 'us-gov-west-1'
      ? 'https://console.amazonaws-us-gov.com'
      : 'https://console.aws.amazon.com';

  const baseUrl = execution
    ? apiUrl('lct', `tenants/${execution.tenantId}/jobManager/executions`)
    : '';

  const {
    doPatch,
    loadingRequests: rescheduleLoading,
    succeededRequests,
    failedRequests,
    clearSucceededRequests,
    clearFailedRequests,
  } = useRestApi(baseUrl, { manual: true });

  const [rescheduleOpen, setRescheduleOpen] = React.useState(false);
  const [rescheduleDateTime, setRescheduleDateTime] = React.useState('');
  const [rescheduleError, setRescheduleError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (succeededRequests.length) {
      setRescheduleOpen(false);
      setRescheduleDateTime('');
      clearSucceededRequests();
      if (props.onRescheduleSuccess) {
        props.onRescheduleSuccess();
      }
    }
  }, [succeededRequests, clearSucceededRequests, props.onRescheduleSuccess, props]);

  React.useEffect(() => {
    if (failedRequests.length) {
      const lastFailed = failedRequests[failedRequests.length - 1];
      setRescheduleError(
        lastFailed?.error?.response?.data?.message || 'Failed to reschedule execution',
      );
      clearFailedRequests();
    }
  }, [failedRequests, clearFailedRequests]);

  const handleReschedule = () => {
    if (!execution || !rescheduleDateTime) return;
    setRescheduleError(null);
    doPatch(execution.executionId, {
      scheduledStartTime: new Date(rescheduleDateTime).toISOString(),
    });
  };

  const {
    doDelete: doCancel,
    loadingRequests: cancelLoading,
    succeededRequests: cancelSucceededRequests,
    failedRequests: cancelFailedRequests,
    clearSucceededRequests: clearCancelSucceededRequests,
    clearFailedRequests: clearCancelFailedRequests,
  } = useRestApi(baseUrl, { manual: true });

  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [cancelError, setCancelError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (cancelSucceededRequests.length) {
      setCancelOpen(false);
      clearCancelSucceededRequests();
      if (props.onCancelSuccess) {
        props.onCancelSuccess();
      }
    }
  }, [cancelSucceededRequests, clearCancelSucceededRequests, props.onCancelSuccess, props]);

  React.useEffect(() => {
    if (cancelFailedRequests.length) {
      const lastFailed = cancelFailedRequests[cancelFailedRequests.length - 1];
      setCancelError(
        lastFailed?.error?.response?.data?.message || t('jobService.cancelExecutionFailed'),
      );
      clearCancelFailedRequests();
    }
  }, [cancelFailedRequests, clearCancelFailedRequests, t]);

  const handleCancelExecution = () => {
    if (!execution) return;
    setCancelError(null);
    doCancel(execution.executionId);
  };

  const {
    doPost: doRedrive,
    loadingRequests: redriveLoading,
    succeededRequests: redriveSucceededRequests,
    failedRequests: redriveFailedRequests,
    clearSucceededRequests: clearRedriveSucceededRequests,
    clearFailedRequests: clearRedriveFailedRequests,
  } = useRestApi(baseUrl, { manual: true });

  const [redriveOpen, setRedriveOpen] = React.useState(false);
  const [redriveError, setRedriveError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (redriveSucceededRequests.length) {
      setRedriveOpen(false);
      clearRedriveSucceededRequests();
      if (props.onRedriveSuccess) {
        props.onRedriveSuccess();
      }
    }
  }, [redriveSucceededRequests, clearRedriveSucceededRequests, props.onRedriveSuccess, props]);

  React.useEffect(() => {
    if (redriveFailedRequests.length) {
      const lastFailed = redriveFailedRequests[redriveFailedRequests.length - 1];
      setRedriveError(
        lastFailed?.error?.response?.data?.message || t('jobService.redriveExecutionFailed'),
      );
      clearRedriveFailedRequests();
    }
  }, [redriveFailedRequests, clearRedriveFailedRequests, t]);

  const handleRedriveExecution = () => {
    if (!execution) return;
    setRedriveError(null);
    doRedrive({}, undefined, `${execution.executionId}/redrive`);
  };

  const redriveCount = (execution as { redriveCount?: number } | undefined)?.redriveCount;

  const canRedrive =
    !!execution &&
    (props.isLearnInstanceExecution ||
      (execution as { tenantType?: string }).tenantType === 'LearnInstance') &&
    FAILED_JOB_STATUSES.has(execution.status) &&
    (execution as { redrivable?: boolean }).redrivable === true;

  const executionDetailsLink =
    execution?.jobSfnArn && execution?.executionId && execution?.region
      ? `${domain}/states/home?region=${execution.jobSfnArn.split(':')[3]}#/v2/executions/details/${execution.jobSfnArn.replace('stateMachine', 'execution')}:${execution.executionId}`
      : undefined;

  let headerAction: React.ReactNode;
  if (execution?.status === Status.PENDING_SCHEDULED) {
    headerAction = (
      <div className={classes.headerActions}>
        <DefaultButton onClick={() => setCancelOpen(true)} data-testid="cancel-execution-button">
          {t('jobService.cancelExecution')}
        </DefaultButton>
        <PrimaryButton onClick={() => setRescheduleOpen(true)} data-testid="reschedule-button">
          {t('jobService.reschedule')}
        </PrimaryButton>
      </div>
    );
  } else if (canRedrive) {
    headerAction = (
      <div className={classes.headerActions}>
        <PrimaryButton
          onClick={() => {
            setRedriveError(null);
            setRedriveOpen(true);
          }}
          data-testid="redrive-button"
        >
          {t('jobService.redriveExecution')}
        </PrimaryButton>
      </div>
    );
  }

  return (
    <>
      <JobCardList
        title="Execution Information"
        loading={loadingDetails || siteDetailsLoading}
        subtitle={execution?.executionId ? `Execution ID: ${execution.executionId}` : ''}
        headerAction={headerAction}
        actions={
          execution?.traceId ? (
            <div className={classes.chipActions}>
              <Chip
                label="View X-Ray Trace"
                onClick={() =>
                  window.open(
                    `https://console.aws.amazon.com/cloudwatch/home?region=us-east-1#xray:traces/${execution?.traceId}`,
                    '_blank',
                    'noopener noreferrer',
                  )
                }
                icon={<OpenInNewWindow />}
              />
              {executionDetailsLink ? (
                <Tooltip
                  title={`To view, log in to account: ${execution.jobSfnArn?.split(':')[4]}`}
                >
                  <Chip
                    label="View State Machine"
                    onClick={() =>
                      window.open(executionDetailsLink, '_blank', 'noopener noreferrer')
                    }
                    icon={<OpenInNewWindow />}
                  />
                </Tooltip>
              ) : null}
            </div>
          ) : undefined
        }
      >
        <ListItem className={classes.listItem}>
          <ListItemIcon>
            <JobStatusIcon status={execution?.status} />
          </ListItemIcon>
          <ListItemText primary="Status" secondary={execution?.status} />
        </ListItem>
        <ListItem className={classes.listItem}>
          <ListItemIcon>
            <Slower />
          </ListItemIcon>
          <ListItemText primary="Start Time" secondary={startDate?.format('lll')} />
        </ListItem>
        {execution?.scheduledStartTime && (
          <ListItem className={classes.listItem}>
            <ListItemIcon>
              <Calendar />
            </ListItemIcon>
            <ListItemText
              primary={t('jobService.scheduledStartTime')}
              secondary={moment(execution.scheduledStartTime).format('lll')}
            />
          </ListItem>
        )}
        <ListItem className={classes.listItem}>
          <ListItemIcon>
            <Faster />
          </ListItemIcon>
          <ListItemText primary="End Time" secondary={endDate?.format('lll') ?? 'Not Reported'} />
        </ListItem>
        <ListItem className={classes.listItem}>
          <ListItemIcon>
            <User />
          </ListItemIcon>
          <ListItemText primary="Created By" secondary={execution?.user ?? 'Unknown User'} />
        </ListItem>
        {redriveCount !== undefined && (
          <ListItem className={classes.listItem} data-testid="redrive-count">
            <ListItemIcon>
              <Refresh />
            </ListItemIcon>
            <ListItemText
              primary={t('jobService.redriveCount')}
              secondary={t('jobService.redrivenTimes', { count: redriveCount })}
            />
          </ListItem>
        )}
        {execution?.parentExecution && (
          <ListItemButton
            className={classes.listItem}
            onClick={() =>
              history.push(
                `/tenants/${execution.parentExecution?.tenantId}/executions/${execution.parentExecution?.executionId}`,
              )
            }
          >
            <ListItemIcon>
              <People />
            </ListItemIcon>
            <ListItemText
              primary="Parent Execution"
              secondary={`Execution ID: ${execution.parentExecution.executionId}`}
            />
          </ListItemButton>
        )}
      </JobCardList>
      <Dialog open={rescheduleOpen} onClose={() => setRescheduleOpen(false)}>
        <DialogTitle onClose={() => setRescheduleOpen(false)} closeLabel={t('global.close')}>
          {t('jobService.rescheduleTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            id="reschedule-datetime"
            label={t('jobService.scheduledStartTime')}
            type="datetime-local"
            value={rescheduleDateTime}
            onChange={(e: any) => setRescheduleDateTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            inputProps={{ min: new Date().toISOString().slice(0, 16) }}
            className={classes.topSpacing}
          />
          {rescheduleError && (
            <Typography color="error" className={classes.topSpacing}>
              {rescheduleError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <DefaultButton onClick={() => setRescheduleOpen(false)}>
            {t('global.cancel')}
          </DefaultButton>
          <PrimaryButton
            onClick={handleReschedule}
            disabled={!rescheduleDateTime || rescheduleLoading}
          >
            {t('jobService.reschedule')}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)}>
        <DialogTitle onClose={() => setCancelOpen(false)} closeLabel={t('global.close')}>
          {t('jobService.cancelExecutionTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography>{t('jobService.cancelExecutionConfirmMessage')}</Typography>
          {cancelError && (
            <Typography color="error" className={classes.topSpacing}>
              {cancelError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <DefaultButton onClick={() => setCancelOpen(false)}>{t('global.cancel')}</DefaultButton>
          <PrimaryButton
            onClick={handleCancelExecution}
            disabled={cancelLoading}
            data-testid="confirm-cancel-execution-button"
          >
            {t('jobService.cancelExecutionConfirm')}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
      <Dialog open={redriveOpen} onClose={() => setRedriveOpen(false)}>
        <DialogTitle onClose={() => setRedriveOpen(false)} closeLabel={t('global.close')}>
          {t('jobService.redriveExecutionTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography>{t('jobService.redriveExecutionConfirmMessage')}</Typography>
          {redriveError && (
            <Typography color="error" className={classes.topSpacing}>
              {redriveError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <DefaultButton onClick={() => setRedriveOpen(false)}>{t('global.cancel')}</DefaultButton>
          <PrimaryButton
            onClick={handleRedriveExecution}
            disabled={redriveLoading}
            data-testid="confirm-redrive-execution-button"
          >
            {t('jobService.redriveExecutionConfirm')}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
